import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { PurchaseOrder, User } from '@/lib/mockData';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { motion } from "framer-motion";
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Eye, ChevronUp, ChevronDown, Check, AlertTriangle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

interface POExcelGridProps {
  purchaseOrders: PurchaseOrder[];
  currentUser: User;
  
  onUpdatePO: (id: number | string, updates: Partial<PurchaseOrder>) => void;
  filters?: {
    poNumber: string;
    location: string;
  };
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

interface CellValidation {
  field: string;
  allowedValues?: string[];
  dataType?: 'text' | 'number' | 'date' | 'select';
  required?: boolean;
}

// Use getFieldPermissions for all RBAC checks
const getFieldPermissions = (role: User['role']) => {
  const baseValidations: Record<string, CellValidation> = {
    // Planner Team fields
    sanityCheck: { field: 'sanityCheck', dataType: 'text' },
    inventoryCheck: { field: 'inventoryCheck', dataType: 'text' },
    fcn: { field: 'fcn', dataType: 'text' },
    fillRate: { field: 'fillRate', dataType: 'text' },
    reasonCode: { field: 'reasonCode', dataType: 'text' },
    
    // Appointment Team fields
    appointmentStatus: { field: 'appointmentStatus', dataType: 'text' },
    physicalAppointmentDate: { field: 'physicalAppointmentDate', dataType: 'text' },
    inwardDateRequested: { field: 'inwardDateRequested', dataType: 'text' },
    reappointmentDate: { field: 'reappointmentDate', dataType: 'text' },
    
    // Sales Order Team fields
    soProcessed: { field: 'soProcessed', dataType: 'text' },
    soStatus: { field: 'soStatus', dataType: 'text' },
    poSentToParty: { field: 'poSentToParty', dataType: 'text' },
    soClosed: { field: 'soClosed', dataType: 'text' },
    
    // Courier Team fields
    pickupDate: { field: 'pickupDate', dataType: 'text' },
    courierPickupFlag: { field: 'courierPickupFlag', dataType: 'text' },
    pickupIntimationSent: { field: 'pickupIntimationSent', dataType: 'text' },
    lrNumber: { field: 'lrNumber', dataType: 'text' },
    reminderSent: { field: 'reminderSent', dataType: 'text' },
    unloaded: { field: 'unloaded', dataType: 'text' },
    podInSap: { field: 'podInSap', dataType: 'text' },
    courierRecall: { field: 'courierRecall', dataType: 'text' },
    deliveryStatus: { field: 'deliveryStatus', dataType: 'text' },
    
    // Invoicing Team fields
    invoiceNumber: { field: 'invoiceNumber', dataType: 'text' },
    invoiceValue: { field: 'invoiceValue', dataType: 'number' },
    sharedToWarehouse: { field: 'sharedToWarehouse', dataType: 'text' },
    
    // Warehouse Team fields
    picklistGeneration: { field: 'picklistGeneration', dataType: 'text' },
    pickAndPack: { field: 'pickAndPack', dataType: 'text' },
    readyToDispatch: { field: 'readyToDispatch', dataType: 'text' },
    handoverToCourier: { field: 'handoverToCourier', dataType: 'text' },
  };

  switch (role) {
    case 'Manager':
      return {
        editable: Object.keys(baseValidations),
        validations: baseValidations
      };
    case 'Planning Team':
      return {
        editable: ['sanityCheck', 'inventoryCheck', 'fcn', 'fillRate', 'reasonCode'],
        validations: baseValidations
      };
    case 'Appointment Team':
      return {
        editable: ['appointmentStatus', 'physicalAppointmentDate', 'inwardDateRequested', 'reappointmentDate'],
        validations: baseValidations
      };
    case 'Sales Order Team':
      return {
        editable: ['soProcessed', 'soStatus', 'poSentToParty', 'soClosed'],
        validations: baseValidations
      };
    case 'Courier Team':
      return {
        editable: ['pickupDate', 'courierPickupFlag', 'pickupIntimationSent', 'lrNumber', 'reminderSent', 'unloaded', 'podInSap', 'courierRecall', 'deliveryStatus'],
        validations: baseValidations
      };
    case 'Invoicing Team':
      return {
        editable: ['invoiceNumber', 'invoiceValue', 'sharedToWarehouse'],
        validations: baseValidations
      };
    case 'Warehouse Team':
      return {
        editable: ['picklistGeneration', 'pickAndPack', 'readyToDispatch', 'handoverToCourier'],
        validations: baseValidations
      };
    default:
      return { editable: [], validations: {} };
  }
};

 

export default function POExcelGrid({
  purchaseOrders,
  currentUser,
  onUpdatePO,
  filters,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
}: POExcelGridProps) {
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [editingCell, setEditingCell] = useState<{ id: number; field: string } | null>(null);
  const [cellValue, setCellValue] = useState('');
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartCell, setDragStartCell] = useState<{ id: number; field: string } | null>(null);
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null);
  const [overrideDialogPO, setOverrideDialogPO] = useState<{ po: PurchaseOrder; team: string } | null>(null);
  const [overrideForm, setOverrideForm] = useState<Partial<PurchaseOrder> | null>(null);
  const { toast } = useToast();

  // Use getFieldPermissions for RBAC
  const { editable, validations } = getFieldPermissions(currentUser.role);


    const formatTimestamp = (timestamp: string) => {
      try {
        return format(new Date(timestamp), 'MMM dd, yyyy HH:mm:ss');
      } catch {
        return String(timestamp);
      }
    };
  
  // Handle sorting
  const handleSort = useCallback((field: string) => {
    setSortConfig(prev => {
      if (prev?.field === field) {
        return { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { field, direction: 'desc' }; // Default to desc to show new POs first
    });
  }, []);

  // Check if team work is completed
  const isTeamCompleted = useCallback((po: PurchaseOrder, team: string): boolean => {
    switch (team) {
      case 'planner':
        return (
          (po as any).sanityCheck === 'Complete' &&
          (po as any).inventoryCheck === 'Complete' &&
          !!(po as any).fcn &&
          !!(po as any).fillRate &&
          !!po.reasonCode 
        );
      case 'appointment':
        return (
          ((po as any).appointmentStatus === 'Complete' || (po as any).appointmentStatus === 'Confirmed') &&
          !!(po as any).physicalAppointmentDate &&
          !!(po as any).inwardDateRequested
        );
      case 'sales':
        return (
          (po as any).soProcessed === 'Yes' &&
          (po as any).soClosed === 'Yes' &&
          !!(po as any).soStatus &&
          !!(po as any).poSentToParty
        );
      case 'courier':
        return (
          (po as any).deliveryStatus === 'Delivered' &&
          !!(po as any).pickupDate &&
          !!(po as any).courierPickupFlag
        );
      case 'invoice':
        return (
          !!(po as any).invoiceNumber &&
          (po as any).sharedToWarehouse === 'Yes' &&
          !!(po as any).invoiceValue
        );
      case 'warehouse':
        return (
          (po as any).handoverToCourier === 'Complete' &&
          !!(po as any).picklistGeneration &&
          !!(po as any).pickAndPack &&
          !!(po as any).readyToDispatch
        );
      default:
        return false;
    }
  }, []);

  // Check if team can start work (previous team completed)
  const canTeamStart = useCallback((po: PurchaseOrder, team: string): boolean => {
    switch (team) {
      case 'planner':
        return true; // Planner can always start
      case 'appointment':
        return isTeamCompleted(po, 'planner');
      case 'sales':
        return isTeamCompleted(po, 'appointment');
      case 'courier':
        return isTeamCompleted(po, 'sales');
      case 'invoice':
        return isTeamCompleted(po, 'courier');
      case 'warehouse':
        return isTeamCompleted(po, 'invoice');
      default:
        return false;
    }
  }, [isTeamCompleted]);

  // Check if team is taking too long (more than 2 days)
  const isTeamDelayed = useCallback((po: PurchaseOrder, team: string): boolean => {
    const createdDate = new Date(po.createdAt);
    const currentDate = new Date();
    const daysDiff = Math.floor((currentDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
    
    // If PO is more than 2 days old and team hasn't completed their work
    return daysDiff > 2 && !isTeamCompleted(po, team);
  }, [isTeamCompleted]);



  //   Excel Grid OPERATIONS START -------------------------------------------------------------------
  // Filter and sort POs
  const filteredAndSortedPOs = useMemo(() => {
    let result = filters ? purchaseOrders.filter(po => {
      const matchesPO = !filters.poNumber || 
        po.poNumber.toLowerCase().includes(filters.poNumber.toLowerCase());
      const matchesLocation = !filters.location || 
        po.location.toLowerCase().includes(filters.location.toLowerCase());
      return matchesPO && matchesLocation;
    }) : [...purchaseOrders];

    // Apply sorting
    if (sortConfig) {
      result.sort((a, b) => {
        if (sortConfig.field === 'poNumber') {
          // Sort by isNew first, then by PO number
          const aIsNew = (a as any).isNew || false;
          const bIsNew = (b as any).isNew || false;
          
          if (aIsNew !== bIsNew) {
            return sortConfig.direction === 'desc' ? 
              (aIsNew ? -1 : 1) : (aIsNew ? 1 : -1);
          }
          
          // If both have same isNew status, sort by PO number
          const aValue = a.poNumber;
          const bValue = b.poNumber;
          
          if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
          if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        }
        
        return 0;
      });
    }

    return result;
  }, [purchaseOrders, filters, sortConfig]);

  const validateCellValue = useCallback((field: string, value: string): boolean => {
    const validation = validations[field];
    if (!validation) return false;

    if (validation.allowedValues && validation.allowedValues.length > 0) {
      return validation.allowedValues.includes(value);
    }

    if (validation.dataType === 'number') {
      return !isNaN(parseFloat(value)) || value === '';
    }

    return true;
  }, [validations]);

  const handleCellEdit = useCallback((po: PurchaseOrder, field: string) => {
    if (!editable.includes(field)) {
      toast({
        title: "Access Denied",
        description: `You don't have permission to edit ${field}`,
        variant: "destructive"
      });
      return;
    }

    setEditingCell({ id: po.id, field });
    setCellValue(String((po as any)[field] || ''));
  }, [editable, toast]);

  const handleCellSave = useCallback(() => {
    if (!editingCell) return;

    const isValid = validateCellValue(editingCell.field, cellValue);
    if (!isValid) {
      const validation = validations[editingCell.field];
      const allowedValues = validation?.allowedValues?.join(', ') || 'valid format';
      toast({
        title: "Invalid Value",
        description: `Only ${allowedValues} allowed for ${editingCell.field}`,
        variant: "destructive"
      });
      return;
    }

    const updates: any = {};
    if (validations[editingCell.field]?.dataType === 'number') {
      updates[editingCell.field] = cellValue === '' ? 0 : parseFloat(cellValue);
    } else {
      updates[editingCell.field] = cellValue;
    }

    onUpdatePO(editingCell.id, updates);
    setEditingCell(null);
    setCellValue('');
  }, [editingCell, cellValue, validateCellValue, validations, onUpdatePO, toast]);

  const handleCellCancel = useCallback(() => {
    setEditingCell(null);
    setCellValue('');
  }, []);

  // Excel-style drag to fill functionality
  const handleDragStart = useCallback((e: React.MouseEvent, po: PurchaseOrder, field: string) => {
    e.preventDefault();
    if (!editable.includes(field)) return;
    
    setIsDragging(true);
    setDragStartCell({ id: po.id, field });
    
    // Add global mouse event listeners
    const handleMouseMove = (e: MouseEvent) => {
      // We'll handle this in the cell hover events
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      setDragStartCell(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [editable]);

  const handleDragEnd = useCallback((targetPO: PurchaseOrder, targetField: string) => {
    if (!isDragging || !dragStartCell || dragStartCell.field !== targetField) return;
    
    const startPO = filteredAndSortedPOs.find(po => po.id === dragStartCell.id);
    if (!startPO) return;
    
    const startValue = (startPO as any)[dragStartCell.field];
    const startIndex = filteredAndSortedPOs.findIndex(po => po.id === dragStartCell.id);
    const endIndex = filteredAndSortedPOs.findIndex(po => po.id === targetPO.id);
    
    if (startIndex === -1 || endIndex === -1 || endIndex <= startIndex) return;
    
    // Fill down the value to all cells between start and end
    for (let i = startIndex + 1; i <= endIndex; i++) {
      const po = filteredAndSortedPOs[i];
      if (po && editable.includes(targetField)) {
        onUpdatePO(po.id, { [targetField]: startValue });
      }
    }
    

  }, [isDragging, dragStartCell, filteredAndSortedPOs, editable, onUpdatePO, toast]);

  const handleCellSelection = useCallback((poId: number, field: string, event: React.MouseEvent) => {
    const cellKey = `${poId}-${field}`;
    
    if (event.ctrlKey || event.metaKey) {
      // Multi-select with Ctrl/Cmd
      const newSelection = new Set(selectedCells);
      if (newSelection.has(cellKey)) {
        newSelection.delete(cellKey);
      } else {
        newSelection.add(cellKey);
      }
      setSelectedCells(newSelection);
    } else if (event.shiftKey && selectedCells.size > 0) {
      // Range select with Shift
      const firstSelected = Array.from(selectedCells)[0];
      const [firstPoId, firstField] = firstSelected.split('-');
      const startIndex = filteredAndSortedPOs.findIndex(po => po.id === parseInt(firstPoId));
      const endIndex = filteredAndSortedPOs.findIndex(po => po.id === poId);
      
      if (firstField === field && startIndex !== -1 && endIndex !== -1) {
        const newSelection = new Set<string>();
        const start = Math.min(startIndex, endIndex);
        const end = Math.max(startIndex, endIndex);
        
        for (let i = start; i <= end; i++) {
          newSelection.add(`${filteredAndSortedPOs[i].id}-${field}`);
        }
        setSelectedCells(newSelection);
      }
    } else {
      // Single select
      setSelectedCells(new Set([cellKey]));
    }
  }, [selectedCells, filteredAndSortedPOs]);

  // Simple fill down function for selected cells
  const handleCellFillDown = useCallback((sourcePoId: number, sourceField: string) => {
    if (!editable.includes(sourceField)) return;
    
    const sourcePO = filteredAndSortedPOs.find(po => po.id === sourcePoId);
    if (!sourcePO) return;
    
    const sourceValue = (sourcePO as any)[sourceField];
    const sourceIndex = filteredAndSortedPOs.findIndex(po => po.id === sourcePoId);
    
    // Fill down to the next 3 rows as an example
    const rowsToFill = 3;
    let filledCount = 0;
    
    for (let i = sourceIndex + 1; i < Math.min(sourceIndex + 1 + rowsToFill, filteredAndSortedPOs.length); i++) {
      const targetPO = filteredAndSortedPOs[i];
      const updates: any = {};
      updates[sourceField] = sourceValue;
      onUpdatePO(targetPO.id, updates);
      filledCount++;
    }
    
    if (filledCount > 0) {
      toast({
        title: "Success",
        description: `Filled ${filledCount} cells below with "${sourceValue}"`,
      });
    }
  }, [editable, filteredAndSortedPOs, onUpdatePO, toast]);

  const handleFillDown = useCallback(() => {
    if (selectedCells.size === 0) return;
    
    const cellsArray = Array.from(selectedCells);
    const groupedByField = cellsArray.reduce((acc, cellKey) => {
      const [poId, field] = cellKey.split('-');
      if (!acc[field]) acc[field] = [];
      acc[field].push(parseInt(poId));
      return acc;
    }, {} as Record<string, number[]>);
    
    Object.entries(groupedByField).forEach(([field, poIds]) => {
      if (!editable.includes(field) || poIds.length < 2) return;
      
      // Sort by PO order in the filtered list
      const sortedPoIds = poIds.sort((a, b) => {
        const aIndex = filteredAndSortedPOs.findIndex(po => po.id === a);
        const bIndex = filteredAndSortedPOs.findIndex(po => po.id === b);
        return aIndex - bIndex;
      });
      
      const firstPO = filteredAndSortedPOs.find(po => po.id === sortedPoIds[0]);
      if (!firstPO) return;
      
      const sourceValue = (firstPO as any)[field];
      
      // Fill all other selected cells with the first cell's value
      sortedPoIds.slice(1).forEach(poId => {
        const updates: any = {};
        updates[field] = sourceValue;
        onUpdatePO(poId, updates);
      });
      
      toast({
        title: "Success",
        description: `Filled ${sortedPoIds.length - 1} cells with "${sourceValue}"`,
      });
    });
  }, [selectedCells, editable, filteredAndSortedPOs, onUpdatePO, toast]);



  //   Excel Grid OPERATIONS END  -------------------------------------------------------------------------

   //   |||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||

  //   RBAC START -----------------------------------------------------------------------------------------

  const getColumnsByRole = () => {
    const baseColumns = [
      { key: 'poNumber', label: 'PO Number', width: '150px', clickable: true },
      { key: 'brand', label: 'Brand', width: '120px' },
      { key: 'location', label: 'Location', width: '100px' },
      { key: 'status', label: 'Status', width: '120px' },
      { key: 'totalAmount', label: 'Amount', width: '100px' },
    ];

    const roleColumns: Record<string, any[]> = {
      'Manager': [
        ...baseColumns,
        { key: 'plannerProgress', label: 'Planner', width: '80px', workflowTeam: 'planner' },
        { key: 'appointmentProgress', label: 'Appointment', width: '100px', workflowTeam: 'appointment' },
        { key: 'salesProgress', label: 'Sales Order', width: '100px', workflowTeam: 'sales' },
        { key: 'courierProgress', label: 'Courier', width: '80px', workflowTeam: 'courier' },
        { key: 'invoiceProgress', label: 'Invoice', width: '80px', workflowTeam: 'invoice' },
        { key: 'warehouseProgress', label: 'Warehouse', width: '100px', workflowTeam: 'warehouse' },
      ],
      'Planning Team': [
        ...baseColumns,
        { key: 'sanityCheck', label: 'Sanity Check', width: '120px', editable: true },
        { key: 'inventoryCheck', label: 'Inventory Check', width: '120px', editable: true },
        { key: 'fcn', label: 'FCN', width: '100px', editable: true },
        { key: 'fillRate', label: 'Enter Fill Rate', width: '120px', editable: true },
        { key: 'reasonCode', label: 'Reason Code', width: '150px', editable: true },
      ],
      'Appointment Team': [
        ...baseColumns,
        { key: 'appointmentStatus', label: 'Status', width: '120px', editable: true },
        { key: 'physicalAppointmentDate', label: 'Physical Appointment Date', width: '180px', editable: true },
        { key: 'inwardDateRequested', label: 'Inward Date Requested', width: '180px', editable: true },
        { key: 'reappointmentDate', label: 'Reappointment Date', width: '150px', editable: true },
      ],
      'Sales Order Team': [
        ...baseColumns,
        { key: 'soProcessed', label: 'SO Processed', width: '120px', editable: true },
        { key: 'soStatus', label: 'SO Status', width: '120px', editable: true },
        { key: 'poSentToParty', label: 'PO Sent to Party', width: '150px', editable: true },
        { key: 'soClosed', label: 'SO Closed', width: '120px', editable: true },
      ],
      'Courier Team': [
        ...baseColumns,
        { key: 'pickupDate', label: 'Pickup Date', width: '120px', editable: true },
        { key: 'courierPickupFlag', label: 'Courier Pickup Flag', width: '150px', editable: true },
        { key: 'pickupIntimationSent', label: 'Pickup Intimation Sent', width: '180px', editable: true },
        { key: 'lrNumber', label: 'LR Number', width: '120px', editable: true },
        { key: 'reminderSent', label: 'Reminder Sent', width: '120px', editable: true },
        { key: 'unloaded', label: 'Unloaded', width: '100px', editable: true },
        { key: 'podInSap', label: 'POD in SAP', width: '120px', editable: true },
        { key: 'courierRecall', label: 'Courier Recall', width: '120px', editable: true },
        { key: 'deliveryStatus', label: 'Delivery Status', width: '120px', editable: true },
      ],
      'Invoicing Team': [
        ...baseColumns,
        { key: 'invoiceNumber', label: 'Invoice Number', width: '150px', editable: true },
        { key: 'invoiceValue', label: 'Invoice Value', width: '120px', editable: true },
        { key: 'sharedToWarehouse', label: 'Shared to Warehouse', width: '180px', editable: true },
      ],
      'Warehouse Team': [
        ...baseColumns,
        { key: 'picklistGeneration', label: 'Picklist Generation', width: '150px', editable: true },
        { key: 'pickAndPack', label: 'Pick & Pack', width: '120px', editable: true },
        { key: 'readyToDispatch', label: 'Ready to Dispatch', width: '150px', editable: true },
        { key: 'handoverToCourier', label: 'Handover to Courier', width: '170px', editable: true },
      ],
    };

  
    return roleColumns[currentUser.role] || baseColumns;
  };

  // Use this to get columns:
  const columns = getColumnsByRole();

  const renderCell = (po: PurchaseOrder, column: any) => {
    const value = (po as any)[column.key];
    const isEditing = editingCell?.id === po.id && editingCell?.field === column.key;
    const isEditable = column.editable && editable.includes(column.key);
    const validation = validations[column.key];

    if (column.key === 'poNumber') {
      return (
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSelectedPO(po)}
            className="text-purple-600 hover:text-purple-800 font-medium cursor-pointer"
          >
            {value}
          </button>
          {(po as any).isNew && (
            <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded-full border border-green-200">
              New
            </span>
          )}
        </div>
      );
    }



    if (column.key === 'status') {
      const getStatusStyle = (status: string) => {
        switch (status?.toLowerCase()) {
          case 'draft':
            return 'text-gray-600';
          case 'pending validation':
            return 'text-orange-600';
          case 'validated':
            return 'text-blue-600';
          case 'in progress':
            return 'text-yellow-600';
          case 'completed':
            return 'text-green-600';
          case 'on hold':
            return 'text-purple-600';
          case 'cancelled':
            return 'text-red-600';
          default:
            return 'text-gray-600';
        }
      };

      return (
        <span className={`text-xs font-medium ${getStatusStyle(value)}`}>
          {value}
        </span>
      );
    }

    if (column.key === 'totalAmount' || column.key === 'soAmount' || column.key === 'invoiceAmount') {
      return `₹${value?.toLocaleString() || 0}`;
    }

    // Manager workflow progress indicators
    if (currentUser.role === 'Manager' && column.workflowTeam) {
      const teamName = column.workflowTeam;
      const isCompleted = isTeamCompleted(po, teamName);
      const canStart = canTeamStart(po, teamName);
      const isDelayed = isTeamDelayed(po, teamName);

      return (
        <div className="flex items-center justify-center">
          {isCompleted ? (
            <div 
              className="w-4 h-4 text-green-600 cursor-pointer hover:text-green-800" 
              onClick={() => {
                   setOverrideDialogPO({ po, team: teamName });
                    setOverrideForm({ ...po });
              }}
              title={`${teamName} team completed - Click to edit`}
            >
              <Check className="w-4 h-4" />
            </div>
          ) : canStart ? (
            isDelayed ? (
              <div 
                className="w-4 h-4 text-red-600 cursor-pointer hover:text-red-800" 
                              onClick={() => {
                   setOverrideDialogPO({ po, team: teamName });
                    setOverrideForm({ ...po });
              }}
                title={`${teamName} team is delayed - Click to override`}
              >
                <AlertTriangle className="w-4 h-4" />
              </div>
            ) : (
              <div 
                className="w-4 h-4 rounded-full bg-yellow-400 cursor-pointer hover:bg-yellow-500" 
                              onClick={() => {
                   setOverrideDialogPO({ po, team: teamName });
                    setOverrideForm({ ...po });
              }}
                title={`${teamName} team can start work - Click to edit`} 
              />
            )
          ) : (
            <div className="w-4 h-4" />
          )}
        </div>
      );
    }

    if (isEditing) {
      const DROPDOWN_OPTIONS: Record<string, string[]> = {
        sanityCheck: ["Pending", "In Progress", "Complete"],
        inventoryCheck: ["Pending", "In Progress", "Complete"],
        appointmentStatus: ["Pending", "Scheduled", "Confirmed", "Complete"],
        soProcessed: ["No", "Yes"],
        soClosed: ["No", "Yes"],
        deliveryStatus: ["Pending", "Scheduled", "In Transit", "Ready", "Delivered"],
        sharedToWarehouse: ["No", "Yes"],
        handoverToCourier: ["Pending", "In Progress", "Complete"],
        picklistGeneration: ["Pending", "In Progress", "Complete"],
        pickAndPack: ["Pending", "In Progress", "Complete"],
        readyToDispatch: ["No", "Yes"],
        courierPickupFlag: ["Pending", "Scheduled", "Ready", "Confirmed", "Complete"],
        soStatus: ["Pending", "Processing", "Active", "Complete"],
        poSentToParty: ["No", "Yes"],
      };

      if (DROPDOWN_OPTIONS[column.key]) {
        return (
          <select
            value={cellValue}
            onChange={e => setCellValue(e.target.value)}
            onBlur={handleCellSave}
            className="h-8 text-xs w-full border rounded"
            autoFocus
          >
            <option value="">Select...</option>
            {DROPDOWN_OPTIONS[column.key].map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      }

      return (
        <Input
          value={cellValue}
          onChange={(e) => setCellValue(e.target.value)}
          className="h-8 text-xs w-full"
          type={validation?.dataType === 'number' ? 'number' : 'text'}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleCellSave();
            if (e.key === 'Escape') handleCellCancel();
          }}
          onBlur={handleCellSave}
          autoFocus
        />
      );
    }

    if (isEditable) {
      const cellKey = `${po.id}-${column.key}`;
      const isSelected = selectedCells.has(cellKey);
      const displayValue = value || '';
      
      return (
        <div
          className={`cursor-pointer hover:bg-gray-100 px-2 py-1 rounded min-h-[24px] flex items-center relative ${
            isSelected ? 'bg-blue-100 border-2 border-blue-500' : ''
          } ${isDragging && dragStartCell?.field === column.key ? 'bg-green-100' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            handleCellSelection(po.id, column.key, e);
          }}
          onDoubleClick={() => handleCellEdit(po, column.key)}
          onContextMenu={(e) => {
            e.preventDefault();
            handleCellFillDown(po.id, column.key);
          }}
          onMouseEnter={() => {
            if (isDragging && dragStartCell?.field === column.key) {
              handleDragEnd(po, column.key);
            }
          }}
        >
          {displayValue ? (
            <span className="text-sm">{displayValue}</span>
          ) : (
            <span className="text-gray-400 text-sm">Click to edit</span>
          )}
          {isSelected && (
            <div 
              className="absolute bottom-0 right-0 w-2 h-2 bg-blue-500 cursor-crosshair hover:bg-blue-600"
              onMouseDown={(e) => handleDragStart(e, po, column.key)}
              title="Drag to fill down"
            ></div>
          )}
        </div>
      );
    }

    return (
      <div className="px-2 py-1 text-sm">
        {value !== undefined && value !== null && value !== '' ? value : '-'}
      </div>
    );
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'd') {
        e.preventDefault();
        handleFillDown();
      }
      if (e.key === 'Escape') {
        setSelectedCells(new Set());
        setEditingCell(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleFillDown]);

  // Pagination controls
  return (
    <div className="space-y-4">
      {/* Excel Grid */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-auto max-h-[600px] border rounded relative">
            <table className="w-full border-collapse">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  {columns.map((column, index) => (
                    <th
                      key={column.key}
                      className={`border border-gray-200 px-2 py-3 text-xs font-medium uppercase tracking-wider ${
                        column.key === 'poNumber' 
                          ? 'bg-purple-100 text-purple-800 sticky left-0 z-20 text-center' 
                          : 'text-gray-700 text-left'
                      }`}
                      style={{ 
                        width: column.width, 
                        minWidth: column.width,
                        ...(column.key === 'poNumber' ? { position: 'sticky', left: 0 } : {})
                      }}
                    >
                      {column.key === 'poNumber' ? (
                        <div 
                          className="flex items-center justify-center cursor-pointer hover:bg-purple-200 p-1 rounded"
                          onClick={() => handleSort('poNumber')}
                        >
                          <span>{column.label}</span>
                          {sortConfig?.field === 'poNumber' && (
                            sortConfig.direction === 'desc' ? 
                              <ChevronDown className="w-4 h-4 ml-1" /> : 
                              <ChevronUp className="w-4 h-4 ml-1" />
                          )}
                        </div>
                      ) : (
                        <>
                          {column.label}
                          {column.editable && <span className="text-blue-500 ml-1">*</span>}
                        </>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white">
                {filteredAndSortedPOs.map((po, index) => (
                  <tr key={po.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={`border border-gray-200 text-xs ${
                          column.key === 'poNumber' 
                            ? 'bg-purple-50 sticky left-0 z-10 text-center' 
                            : ''
                        }`}
                        style={{ 
                          width: column.width, 
                          minWidth: column.width,
                          ...(column.key === 'poNumber' ? { position: 'sticky', left: 0 } : {})
                        }}
                      >
                        {renderCell(po, column)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* PO Details Modal */}
      <Dialog open={!!selectedPO}  onOpenChange={() => setSelectedPO(null)}>
        <DialogContent aria-describedby="PO Detailed Info" className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader className="px-4 py-3 border-b bg-gradient-to-r from-blue-50 to-purple-50">
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Eye className="h-5 w-5 text-blue-600" />
              PO Details: {selectedPO?.poNumber}
            </DialogTitle>
          </DialogHeader>
          
          {selectedPO && (
            <div className="px-4 py-4 space-y-6 bg-gray-50/30">
              {/* PO Workflow Status */}
              <div className="space-y-2">
      <div>
        <h3 className="text-sm font-bold text-gray-900">PO Workflow Status</h3>
        <p className="text-xs text-gray-500">Real-time visualization of the PO process workflow</p>
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-2 rounded border shadow-sm">
        <div className="grid grid-cols-6 gap-2">
          {/* Planning */}
          <div className="text-center">
            <div className="w-8 h-8 mx-auto mb-0.5 bg-green-100 rounded flex items-center justify-center">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="font-medium text-xs">Planning</div>
            <div className="text-xs text-gray-600 mb-0.5">Sanity Check</div>
            {getStatusBadge(selectedPO?.PlannerStatus)}
          </div>

          {/* Appointment */}
          <div className="text-center">
            <div className="w-8 h-8 mx-auto mb-0.5 bg-green-100 rounded flex items-center justify-center">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="font-medium text-xs">Appointment</div>
            <div className="text-xs text-gray-600 mb-0.5">Scheduling</div>
            {getStatusBadge(selectedPO?.AppointmentStatus)}
          </div>

          {/* Sales Order */}
          <div className="text-center">
            <div className="w-8 h-8 mx-auto mb-0.5 bg-blue-100 rounded flex items-center justify-center">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="font-medium text-xs">Sales Order</div>
            <div className="text-xs text-gray-600 mb-0.5">Processing</div>
            {getStatusBadge(selectedPO?.SalesOrderStatus)}
          </div>

          {/* Courier Operations */}
          <div className="text-center">
            <div className="w-8 h-8 mx-auto mb-0.5 bg-orange-100 rounded flex items-center justify-center">
              <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <div className="font-medium text-xs">Courier</div>
            <div className="text-xs text-gray-600 mb-0.5">Transport</div>
            {getStatusBadge(selectedPO?.CourierStatus)}
          </div>

          {/* Invoicing */}
          <div className="text-center">
            <div className="w-8 h-8 mx-auto mb-0.5 bg-gray-100 rounded flex items-center justify-center">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="font-medium text-xs">Invoicing</div>
            <div className="text-xs text-gray-600 mb-0.5">Bill Generation</div>
            {getStatusBadge(selectedPO?.InvoiceStatus)}
          </div>

          {/* Warehouse Dispatch */}
          <div className="text-center">
            <div className="w-8 h-8 mx-auto mb-0.5 bg-gray-100 rounded flex items-center justify-center">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <div className="font-medium text-xs">Warehouse</div>
            <div className="text-xs text-gray-600 mb-0.5">Fulfillment</div>
            {getStatusBadge(selectedPO?.WarehouseStatus)}
          </div>
        </div>
      </div>
    </div>
    

              {/* PO Summary */}
              <div className="space-y-2">
                <h3 className="text-base font-bold text-gray-900">PO Summary</h3>
                <div className="bg-white rounded shadow border border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-3 py-1">
                    <h4 className="text-xs font-semibold text-white">Purchase Order Information</h4>
                  </div>
                  <div className="p-2">
                    <div className="grid grid-cols-4 gap-2">
                      <div className="bg-blue-50 p-2 rounded border-l-2 border-blue-500">
                        <span className="text-xs font-medium text-blue-700">Total Value</span>
                        <div className="text-sm font-bold text-blue-900">₹{selectedPO.totalAmount.toLocaleString()}</div>
                      </div>
                      <div className="bg-green-50 p-2 rounded border-l-2 border-green-500">
                        <span className="text-xs font-medium text-green-700">Quantity</span>
                        <div className="text-sm font-bold text-green-900">100</div>
                      </div>
                      <div className="bg-purple-50 p-2 rounded border-l-2 border-purple-500">
                        <span className="text-xs font-medium text-purple-700">Order Date</span>
                        {/* <div className="text-sm font-bold text-purple-900">{selectedPO.createdAt?.toLocaleDateString}</div> */}
                        <div className="text-sm font-bold text-purple-900">{
                          selectedPO.createdAt
                            ? (selectedPO.createdAt instanceof Date
                                ? selectedPO.createdAt.toLocaleDateString()
                                : new Date(selectedPO.createdAt).toLocaleDateString())
                            : '-'
                        }</div>
                      </div>
                      <div className="bg-orange-50 p-2 rounded border-l-2 border-orange-500">
                        <span className="text-xs font-medium text-orange-700">Expected Delivery</span>
                        <div className="text-sm font-bold text-purple-900">{
                          selectedPO.estimatedDelivery
                            ? (selectedPO.estimatedDelivery instanceof Date
                                ? selectedPO.estimatedDelivery.toLocaleDateString()
                                : new Date(selectedPO.estimatedDelivery).toLocaleDateString())
                            : '-'
                        }</div>
                 
                        {/* <div className="text-sm font-bold text-orange-900">{selectedPO.estimatedDelivery?.toLocaleDateString}</div> */}
                      </div>
                      <div className="bg-indigo-50 p-2 rounded border-l-2 border-indigo-500">
                        <span className="text-xs font-medium text-indigo-700">Category</span>
                        <div className="text-sm font-bold text-indigo-900">{selectedPO.category}</div>
                      </div>
                      <div className="bg-pink-50 p-2 rounded border-l-2 border-pink-500">
                        <span className="text-xs font-medium text-pink-700">Supplier</span>
                        <div className="text-sm font-bold text-pink-900">{selectedPO.supplier}</div>
                      </div>
                      <div className="bg-teal-50 p-2 rounded border-l-2 border-teal-500 col-span-2">
                        <span className="text-xs font-medium text-teal-700">Brand</span>
                        <div className="text-sm font-bold text-teal-900">{selectedPO.brand}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* PO Items (Approved Data) */}
              {/* <div className="space-y-2">
                <h3 className="text-sm font-bold text-gray-900">PO Items </h3>
                <div className="bg-white rounded shadow border border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-green-500 to-blue-600 px-2 py-1">
                    <h4 className="text-xs font-semibold text-white">Item Details & Pricing</h4>
                  </div>
                  <div className="overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-1 py-1 text-left font-semibold text-gray-800 border-b text-xs">Code</th>
                          <th className="px-1 py-1 text-left font-semibold text-gray-800 border-b text-xs">HSN</th>
                          <th className="px-1 py-1 text-left font-semibold text-gray-800 border-b text-xs">UPC</th>
                          <th className="px-1 py-1 text-left font-semibold text-gray-800 border-b text-xs">Item Name</th>
                          <th className="px-1 py-1 text-left font-semibold text-gray-800 border-b text-xs">Brand</th>
                          <th className="px-1 py-1 text-left font-semibold text-gray-800 border-b text-xs">Category</th>
                          <th className="px-1 py-1 text-left font-semibold text-gray-800 border-b text-xs">MRP</th>
                          <th className="px-1 py-1 text-left font-semibold text-gray-800 border-b text-xs">Rate</th>
                          <th className="px-1 py-1 text-left font-semibold text-gray-800 border-b text-xs">Qty</th>
                          <th className="px-1 py-1 text-left font-semibold text-gray-800 border-b text-xs">Free</th>
                          <th className="px-1 py-1 text-left font-semibold text-gray-800 border-b text-xs">Total</th>
                          <th className="px-1 py-1 text-left font-semibold text-gray-800 border-b text-xs">GST%</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        <tr className="hover:bg-blue-50 transition-colors">
                          <td className="px-1 py-1 text-gray-700 font-medium text-xs">10089448</td>
                          <td className="px-1 py-1 text-gray-700 text-xs">33049910</td>
                          <td className="px-1 py-1 text-gray-700 text-xs">8904091128556</td>
                          <td className="px-1 py-1 text-blue-600 font-semibold text-xs">Himalaya Baby Wipes</td>
                          <td className="px-1 py-1 text-gray-700 text-xs">Himalaya</td>
                          <td className="px-1 py-1 text-gray-700 text-xs">Baby Care</td>
                          <td className="px-1 py-1 text-gray-700 font-medium text-xs">₹180</td>
                          <td className="px-1 py-1 text-green-600 font-medium text-xs">₹144</td>
                          <td className="px-1 py-1 text-gray-700 font-medium text-xs">100</td>
                          <td className="px-1 py-1 text-purple-600 font-medium text-xs">10</td>
                          <td className="px-1 py-1 text-blue-600 font-bold text-xs">₹14,400</td>
                          <td className="px-1 py-1 text-gray-700 text-xs">12%</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div> */}


            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manager Override Dialog */}
      <Dialog
        open={!!overrideDialogPO}
        onOpenChange={(open) => {
          if (!open) {
            setOverrideDialogPO(null);
            setOverrideForm(null);
          }
        }}
      >
        <DialogContent aria-describedby="PO Detailed Info" className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader className="px-4 py-3 border-b bg-gradient-to-r from-red-50 to-orange-50">
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Manager Override - {overrideDialogPO?.team} Team
              <Badge variant="destructive" className="ml-2">Delayed</Badge>
            </DialogTitle>
          </DialogHeader>
          
          {overrideDialogPO && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-red-700">Mandatory fields to complete:</h4>
              <ul className="list-disc list-inside text-xs text-red-600">
                {getMissingFields(overrideDialogPO.po, overrideDialogPO.team).map(field => (
                  <li key={field}>{field}</li>
                ))}
              </ul>
            </div>
          )}

          {overrideDialogPO && (
            <div className="px-4 py-4 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">
                  <strong>PO {overrideDialogPO.po.poNumber}</strong> - {overrideDialogPO.team} team is taking more than 2 days. 
                  You can override and update their fields below.
                </p>
              </div>

              {overrideDialogPO.team === 'planner' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Sanity Check</label>
                    <select 
                      className="w-full p-2 border rounded text-sm"
                      value={overrideForm?.sanityCheck || ''}
                      onChange={(e) => setOverrideForm(f => ({ ...f, sanityCheck: e.target.value }))}
                    >
                      <option value="">Select...</option>
                      <option value="Pending">Pending</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Complete">Complete</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Inventory Check</label>
                    <select 
                      className="w-full p-2 border rounded text-sm"
                      value={overrideForm?.inventoryCheck || ''}
                      onChange={(e) => setOverrideForm(f => ({ ...f, inventoryCheck: e.target.value }))}
                    >
                      <option value="">Select...</option>
                      <option value="Pending">Pending</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Complete">Complete</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">FCN</label>
                    <Input 
                      className="text-sm"
                      value={overrideForm?.fcn || ''}
                      onChange={(e) => setOverrideForm(f => ({ ...f, fcn: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Fill Rate</label>
                    <Input 
                      className="text-sm"
                      value={overrideForm?.fillRate || ''}
                      onChange={(e) => setOverrideForm(f => ({ ...f, fillRate: e.target.value }))}
                    />
                  </div>
                  <div>
                <label className="block text-sm font-medium mb-1">Reason Code</label>
                <Input
                  className="text-sm"
                  value={overrideForm?.reasonCode || ''}
                  onChange={(e) => setOverrideForm(f => ({ ...f, reasonCode: e.target.value }))}
                />
              </div>
                </div>
              )}

              {overrideDialogPO.team === 'appointment' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Appointment Status</label>
                    <select 
                      className="w-full p-2 border rounded text-sm"
                      value={overrideForm?.appointmentStatus || ''}
                      onChange={(e) => setOverrideForm(f => ({ ...f, appointmentStatus: e.target.value }))}
                    >
                      <option value="">Select...</option>
                      <option value="Pending">Pending</option>
                      <option value="Scheduled">Scheduled</option>
                      <option value="Confirmed">Confirmed</option>
                      <option value="Complete">Complete</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Physical Appointment Date</label>
                    <Input 
                      type="date"
                      className="text-sm"
                      value={overrideForm?.physicalAppointmentDate || ''}
                      onChange={(e) => setOverrideForm(f => ({ ...f, physicalAppointmentDate: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Inward Date Requested</label>
                    <Input 
                      type="date"
                      className="text-sm"
                      value={overrideForm?.inwardDateRequested || ''}
                      onChange={(e) => setOverrideForm(f => ({ ...f, inwardDateRequested: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Reappointment Date</label>
                    <Input 
                      type="date"
                      className="text-sm"
                      value={overrideForm?.reappointmentDate || ''}
                      onChange={(e) => setOverrideForm(f => ({ ...f, reappointmentDate: e.target.value }))}
                    />
                  </div>
                </div>
              )}

              {overrideDialogPO.team === 'sales' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">SO Processed</label>
                    <select 
                      className="w-full p-2 border rounded text-sm"
                      value={overrideForm?.soProcessed || ''}
                      onChange={(e) => setOverrideForm(f => ({ ...f, soProcessed: e.target.value }))}
                    >
                      <option value="">Select...</option>
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">SO Status</label>
                    <select 
                      className="w-full p-2 border rounded text-sm"
                      value={overrideForm?.soStatus || ''}
                      onChange={(e) => setOverrideForm(f => ({ ...f, soStatus: e.target.value }))}
                    >
                      <option value="">Select...</option>
                      <option value="Pending">Pending</option>
                      <option value="Processing">Processing</option>
                      <option value="Active">Active</option>
                      <option value="Complete">Complete</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">PO Sent to Party</label>
                    <select 
                      className="w-full p-2 border rounded text-sm"
                      value={overrideForm?.poSentToParty || ''}
                      onChange={(e) => setOverrideForm(f => ({ ...f, poSentToParty: e.target.value }))}
                    >
                      <option value="">Select...</option>
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">SO Closed</label>
                    <select 
                      className="w-full p-2 border rounded text-sm"
                      value={overrideForm?.soClosed || ''}
                      onChange={(e) => setOverrideForm(f => ({ ...f, soClosed: e.target.value }))}
                    >
                      <option value="">Select...</option>
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </div>
                </div>
              )}

              {overrideDialogPO.team === 'courier' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Pickup Date</label>
                    <Input 
                      type="date"
                      className="text-sm"
                      value={overrideForm?.pickupDate || ''}
                      onChange={(e) => setOverrideForm(f => ({ ...f, pickupDate: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Courier Pickup Flag</label>
                    <select 
                      className="w-full p-2 border rounded text-sm"
                      value={overrideForm?.courierPickupFlag || ''}
                      onChange={(e) => setOverrideForm(f => ({ ...f, courierPickupFlag: e.target.value }))}
                    >
                      <option value="">Select...</option>
                      <option value="Pending">Pending</option>
                      <option value="Scheduled">Scheduled</option>
                      <option value="Ready">Ready</option>
                      <option value="Confirmed">Confirmed</option>
                      <option value="Complete">Complete</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">LR Number</label>
                    <Input 
                      className="text-sm"
                      value={overrideForm?.lrNumber || ''}
                      onChange={(e) => setOverrideForm(f => ({ ...f, lrNumber: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Delivery Status</label>
                    <select 
                      className="w-full p-2 border rounded text-sm"
                      value={overrideForm?.deliveryStatus || ''}
                      onChange={(e) => setOverrideForm(f => ({ ...f, deliveryStatus: e.target.value }))}
                    >
                      <option value="">Select...</option>
                      <option value="Pending">Pending</option>
                      <option value="Scheduled">Scheduled</option>
                      <option value="In Transit">In Transit</option>
                      <option value="Ready">Ready</option>
                      <option value="Delivered">Delivered</option>
                    </select>
                  </div>
                </div>
              )}

              {overrideDialogPO.team === 'invoice' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Invoice Number</label>
                    <Input 
                      className="text-sm"
                      value={overrideForm?.invoiceNumber || ''}
                      onChange={(e) => setOverrideForm(f => ({ ...f, invoiceNumber: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Invoice Value</label>
                    <Input 
                      type="number"
                      className="text-sm"
                      value={overrideForm?.invoiceValue || ''}
                      onChange={(e) => setOverrideForm(f => ({ ...f, invoiceValue: parseFloat(e.target.value) || 0 }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Shared to Warehouse</label>
                    <select 
                      className="w-full p-2 border rounded text-sm"
                      value={overrideForm?.sharedToWarehouse || ''}
                      onChange={(e) => setOverrideForm(f => ({ ...f, sharedToWarehouse: e.target.value }))}
                    >
                      <option value="">Select...</option>
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </div>
                </div>
              )}

              {overrideDialogPO.team === 'warehouse' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Picklist Generation</label>
                    <select 
                      className="w-full p-2 border rounded text-sm"
                      value={overrideForm?.picklistGeneration || ''}
                      onChange={(e) => setOverrideForm(f => ({ ...f, picklistGeneration: e.target.value }))}
                    >
                      <option value="">Select...</option>
                      <option value="Pending">Pending</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Complete">Complete</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Pick & Pack</label>
                    <select 
                      className="w-full p-2 border rounded text-sm"
                      value={overrideForm?.pickAndPack || ''}
                      onChange={(e) => setOverrideForm(f => ({ ...f, pickAndPack: e.target.value }))}
                    >
                      <option value="">Select...</option>
                      <option value="Pending">Pending</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Complete">Complete</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Ready to Dispatch</label>
                    <select 
                      className="w-full p-2 border rounded text-sm"
                      value={overrideForm?.readyToDispatch || ''}
                      onChange={(e) => setOverrideForm(f => ({ ...f, readyToDispatch: e.target.value }))}
                    >
                      <option value="">Select...</option>
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Handover to Courier</label>
                    <select 
                      className="w-full p-2 border rounded text-sm"
                      value={overrideForm?.handoverToCourier || ''}
                      onChange={(e) => setOverrideForm(f => ({ ...f, handoverToCourier: e.target.value }))}
                    >
                      <option value="">Select...</option>
                      <option value="Pending">Pending</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Complete">Complete</option>
                    </select>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4 border-t">
                <button 
                  className="px-4 py-2 text-sm border rounded hover:bg-gray-50"
                  onClick={() => setOverrideDialogPO(null)}
                >
                  Close
                </button>
                <button 
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                  onClick={() => {
                    if (overrideDialogPO && overrideForm) {
    onUpdatePO(overrideDialogPO.po.id, overrideForm);
    toast({
      title: "Changes Saved",
      description: `${overrideDialogPO.team} team fields updated successfully.`,
    });
    setOverrideDialogPO(null);
    setOverrideForm(null);
  }
}}
                >
                  Save Changes
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pagination Controls */}
<div className="flex items-center justify-between mt-4">
 <div className="flex items-center gap-2">
  <label className="text-xs">Rows per page:</label>


<Select
  value={String(pageSize)}
  onValueChange={(value) => onPageSizeChange(Number(value))} // ✅ value is string, convert to number
>
  <SelectTrigger className="w-20">
    <SelectValue placeholder="Rows" />
  </SelectTrigger>
  <SelectContent>
    <SelectItem value="10">10</SelectItem>
    <SelectItem value="25">25</SelectItem>
    <SelectItem value="50">50</SelectItem>
    <SelectItem value="100">100</SelectItem>
  </SelectContent>
</Select>

 
</div>

  <div>
    <Button
      variant="outline"
      disabled={page === 1}
      onClick={() => onPageChange(page - 1)}
      className="mr-2"
    >
      Prev
    </Button>
    <span className="text-xs">
      Page {page} of {Math.max(1, Math.ceil(totalCount / pageSize))}
    </span>
    <Button
      variant="outline"
      disabled={page >= Math.ceil(totalCount / pageSize)}
      onClick={() => onPageChange(page + 1)}
      className="ml-2"
    >
      Next
    </Button>
  </div>
</div>
    </div>
  );
}

// Add inside POExcelGrid.tsx

const badgeVariants = {
  initial: { opacity: 0, scale: 0.8, y: 10 },
  animate: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } },
};
const getStatusBadge = (status: string | undefined) => {
  const value = status?.toLowerCase().trim();

  const renderBadge = (text: string, className: string) => (
    <motion.div
      key={text}
      variants={badgeVariants}
      initial="initial"
      animate="animate"
    >
      <Badge className={`${className} text-white text-xs px-4 border-none py-2 my-4 `}>
        {text}
      </Badge>
    </motion.div>
  );

  if (!value || value === "pending") {
    return renderBadge("Pending", "bg-orange-500");
  }

  if (value === "completed") {
    return renderBadge("Completed", "bg-green-500");
  }

  if (value === "validated") {
    return renderBadge("Validated", "bg-blue-500");
  }

  return renderBadge("Not Started", "bg-gray-400");
};

function getMissingFields(po: PurchaseOrder, team: string): string[] {
  switch (team) {
    case 'planner':
      return [
        po.sanityCheck === 'Complete' ? null : 'Sanity Check',
        po.inventoryCheck === 'Complete' ? null : 'Inventory Check',
        po.fcn ? null : 'FCN',
        po.fillRate ? null : 'Fill Rate',
        po.reasonCode ? null : 'Reason Code', // <-- add this line
      ].filter(Boolean) as string[];
    case 'appointment':
      return [
        (po.appointmentStatus === 'Complete' || po.appointmentStatus === 'Confirmed') ? null : 'Appointment Status',
        po.physicalAppointmentDate ? null : 'Physical Appointment Date',
        po.inwardDateRequested ? null : 'Inward Date Requested',
      ].filter(Boolean) as string[];
    case 'sales':
      return [
        po.soProcessed === 'Yes' ? null : 'SO Processed',
        po.soClosed === 'Yes' ? null : 'SO Closed',
        po.soStatus ? null : 'SO Status',
        po.poSentToParty ? null : 'PO Sent to Party',
      ].filter(Boolean) as string[];
    case 'courier':
      return [
        po.deliveryStatus === 'Delivered' ? null : 'Delivery Status',
        po.pickupDate ? null : 'Pickup Date',
        po.courierPickupFlag ? null : 'Courier Pickup Flag',
      ].filter(Boolean) as string[];
    case 'invoice':
      return [
        po.invoiceNumber ? null : 'Invoice Number',
        po.sharedToWarehouse === 'Yes' ? null : 'Shared to Warehouse',
        po.invoiceValue ? null : 'Invoice Value',
      ].filter(Boolean) as string[];
    case 'warehouse':
      return [
        po.handoverToCourier === 'Complete' ? null : 'Handover to Courier',
        po.picklistGeneration ? null : 'Picklist Generation',
        po.pickAndPack ? null : 'Pick & Pack',
        po.readyToDispatch ? null : 'Ready to Dispatch',
      ].filter(Boolean) as string[];
    default:
      return [];
  }
}